import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { AlertService } from '../alert/alert.service';
import { ConfigService } from '../../config/config.service';

/**
 * Log levels for application logging
 */
export enum LogLevel {
  Off = 0,
  Error = 1,
  Warn = 2,
  Info = 3,
  Debug = 4,
  Trace = 5
}

/**
 * Maps string log levels to LogLevel enum values
 */
export const LOG_LEVEL_MAP: Record<string, LogLevel> = {
  'Off': LogLevel.Off,
  'Error': LogLevel.Error,
  'Warn': LogLevel.Warn,
  'Info': LogLevel.Info,
  'Debug': LogLevel.Debug,
  'Trace': LogLevel.Trace
}

/**
 * Configuration options for the LoggingService
 */
export interface LoggingConfig {
  level: LogLevel;
  includeTimestamp: boolean;
  logWithGrouping: boolean;
  includeSource: boolean;
  displayAlerts: boolean;
  serverLogging: boolean;
  serverLogLevel: LogLevel;
  serverLogEndpoint?: string;
}

/**
 * Default configuration for the LoggingService
 */
const DEFAULT_CONFIG: LoggingConfig = {
  level: LogLevel.Debug,
  includeTimestamp: true,
  logWithGrouping: true, 
  includeSource: true,
  displayAlerts: false,
  serverLogging: false,
  serverLogLevel: LogLevel.Error
};

/**
 * Service for centralized application logging
 * 
 * This service provides logging methods with different severity levels
 * and controls log output based on the current environment.
 */
@Injectable({
  providedIn: 'root'
})
export class LoggingService {
  private config: LoggingConfig;
  private readonly PERSISTENT_LOG_KEY = 'app-logs';
  private buffer: { level: LogLevel, message: string, data?: any }[] = [];
  private bufferSize = 100;
  private isProduction = environment.production;
  
  constructor(
    private alertService: AlertService,
    private configService: ConfigService
  ) {
    // Initialize with default config
    this.config = { ...DEFAULT_CONFIG };
    
    // Override with config from ConfigService if available
    this.updateConfigFromService();
    
    // Initial log (using direct console.info to avoid circular calls in tests)
    if (this.shouldLogToConsole(LogLevel.Info)) {
      const initData = { level: LogLevel[this.config.level], production: this.isProduction };
      if (this.config.logWithGrouping) {
        console.groupCollapsed(`[LoggingService] Logging service initialized`);
        console.log(initData);
        console.groupEnd();
      } else {
        console.info(`[LoggingService] Logging service initialized`, initData);
      }
    }
  }

  /**
   * Set the current logging configuration
   */
  setConfig(config: Partial<LoggingConfig>): void {
    const oldConfig = { ...this.config };
    
    this.config = {
      ...this.config,
      ...config
    };
    
    // Use direct console.debug to avoid potential infinite loops
    // during testing when the log level is changing
    if (this.shouldLogToConsole(LogLevel.Debug)) {
      if (this.config.logWithGrouping) {
        console.groupCollapsed('[LoggingService] Logging configuration updated');
        console.log('Previous:', oldConfig);
        console.log('Current:', this.config);
        console.groupEnd();
      } else {
        console.debug('[LoggingService] Logging configuration updated', { 
          previous: oldConfig, 
          current: this.config 
        });
      }
    }
  }

  /**
   * Get the current logging configuration
   */
  getConfig(): LoggingConfig {
    return { ...this.config };
  }

  /**
   * Log a message with DEBUG level
   */
  debug(source: string, message: string, data?: any): void {
    this.logMessage(LogLevel.Debug, source, message, data);
  }

  /**
   * Log a message with INFO level
   */
  info(source: string, message: string, data?: any): void {
    this.logMessage(LogLevel.Info, source, message, data);
  }

  /**
   * Log a message with WARN level
   */
  warn(source: string, message: string, data?: any): void {
    this.logMessage(LogLevel.Warn, source, message, data);
  }

  /**
   * Log a message with ERROR level
   */
  error(source: string, message: string, data?: any): void {
    this.logMessage(LogLevel.Error, source, message, data);
    
    // Add to error buffer for potential reporting
    this.addToBuffer(LogLevel.Error, message, data);
    
    // Optionally show alert for errors
    if (this.config.displayAlerts) {
      this.alertService.error('Application Error', message);
    }
    
    // Optionally send to server logging endpoint
    if (this.config.serverLogging && this.config.serverLogLevel >= LogLevel.Error) {
      this.sendToServer(LogLevel.Error, source, message, data);
    }
  }

  /**
   * Trace method for detailed debugging (highest verbosity)
   */
  trace(source: string, message: string, data?: any): void {
    this.logMessage(LogLevel.Trace, source, message, data);
  }

  /**
   * Clear all saved logs
   */
  clearLogs(): void {
    this.buffer = [];
    localStorage.removeItem(this.PERSISTENT_LOG_KEY);
  }

  /**
   * Get recent logs as a downloadable string
   */
  getLogsAsString(): string {
    return this.buffer
      .map(log => `[${LogLevel[log.level]}] ${log.message} ${log.data ? JSON.stringify(log.data) : ''}`)
      .join('\n');
  }

  /**
   * Download logs as a text file
   */
  downloadLogs(filename: string = 'application-logs.txt'): void {
    const logs = this.getLogsAsString();
    const blob = new Blob([logs], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Core logging method that handles all log levels
   */
  private logMessage(level: LogLevel, source: string, message: string, data?: any): void {
    if (level > this.config.level) return;
    
    const timestamp = this.config.includeTimestamp ? new Date().toISOString() : '';
    const sourceStr = this.config.includeSource ? `[${source}]` : '';
    const logPrefix = timestamp ? `${timestamp} ${sourceStr}` : sourceStr;
    
    if (this.shouldLogToConsole(level)) {
      if (this.config.logWithGrouping && (data || level >= LogLevel.Warn)) {
        const groupMethod = (level >= LogLevel.Warn ? 'groupCollapsed' : 'group') as keyof Console;
        const logMethod = this.getLogMethod(level) as keyof Console;
        // Use grouping for complex logs
        (console[groupMethod] as Function).apply(console, [`${logPrefix} ${message}`]);
        if (data !== undefined) {
          console.log(data);
        }
        console.groupEnd();
      } else {
        const logMethod = this.getLogMethod(level) as keyof Console;
        if (data !== undefined) {
          (console[logMethod] as Function).apply(console, [`${logPrefix} ${message}`, data]);
        } else {
          (console[logMethod] as Function).apply(console, [`${logPrefix} ${message}`]);
        }
      }
    }
    
    // Save non-debug logs to buffer for potential reporting
    if (level <= LogLevel.Info) {
      this.addToBuffer(level, `${sourceStr} ${message}`, data);
    }
    
    // Send to server if configured (and meets the server log level)
    if (this.config.serverLogging && level <= this.config.serverLogLevel) {
      this.sendToServer(level, source, message, data);
    }
  }
  
  /**
   * Determine if we should output to console based on current configuration
   */
  private shouldLogToConsole(level: LogLevel): boolean {
    // Don't log if level is Off
    if (this.config.level === LogLevel.Off) {
      return false;
    }
    
    // In production, we might want to completely disable console.* calls
    if (this.isProduction && 
        this.configService.config?.logging?.disableAllConsoleOutput === true) {
      return false;
    }
    
    // Check if the message's level is within our configured level
    return level <= this.config.level;
  }

  /**
   * Get the appropriate console method for a log level
   */
  private getLogMethod(level: LogLevel): string {
    switch (level) {
      case LogLevel.Trace:
        return 'trace';
      case LogLevel.Debug:
        return 'debug';
      case LogLevel.Info:
        return 'info';
      case LogLevel.Warn:
        return 'warn';
      case LogLevel.Error:
        return 'error';
      default:
        return 'log';
    }
  }
  
  /**
   * Add a log entry to the buffer (for potential export/reporting)
   */
  private addToBuffer(level: LogLevel, message: string, data?: any): void {
    this.buffer.push({ level, message, data });
    
    // Keep buffer size in check
    if (this.buffer.length > this.bufferSize) {
      this.buffer.shift();
    }
  }
  
  /**
   * Send a log to the server (if configured)
   */
  private sendToServer(level: LogLevel, source: string, message: string, data?: any): void {
    if (!this.config.serverLogEndpoint) return;
    
    // Simple implementation - can be expanded as needed
    const logData = {
      level: LogLevel[level],
      source,
      message,
      timestamp: new Date().toISOString(),
      data,
      userAgent: navigator.userAgent,
      url: window.location.href
    };
    
    // Use fetch to send log data
    fetch(this.config.serverLogEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(logData),
      // Don't wait for or handle the response
      keepalive: true
    }).catch(() => {
      // Silently fail - we don't want logging failures to cause more issues
    });
  }
  
  /**
   * Update configuration from the ConfigService
   */
  private updateConfigFromService(): void {
    if (!this.configService.config?.logging) return;
    
    const loggingConfig = this.configService.config.logging;
    
    this.config = {
      ...this.config,
      level: loggingConfig.level ? LOG_LEVEL_MAP[loggingConfig.level] : this.config.level,
      includeTimestamp: loggingConfig.includeTimestamp ?? this.config.includeTimestamp,
      logWithGrouping: loggingConfig.logWithGrouping ?? this.config.logWithGrouping,
      includeSource: loggingConfig.includeSource ?? this.config.includeSource,
      displayAlerts: loggingConfig.displayAlerts ?? this.config.displayAlerts,
      serverLogging: loggingConfig.serverLogging ?? this.config.serverLogging,
      serverLogLevel: loggingConfig.serverLogLevel ? LOG_LEVEL_MAP[loggingConfig.serverLogLevel] : this.config.serverLogLevel,
      serverLogEndpoint: loggingConfig.serverLogEndpoint || this.config.serverLogEndpoint
    };
    
    // In production, default to more restrictive logging if not explicitly configured
    if (this.isProduction && !loggingConfig.level) {
      this.config.level = LogLevel.Error;
      this.config.includeSource = false;
      this.config.logWithGrouping = false;
    }
    
    // Check for complete console output disabling
    if (loggingConfig.disableAllConsoleOutput) {
      this.config.level = LogLevel.Off;
    }
  }
}
